import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO, track } from "@/components/cobrain/tokens";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { ArrowRight, Check, XMark } from "@/components/cobrain/icons";
import { HeroConsole } from "@/components/cobrain/HeroConsole";
import { AmbientBackdrop } from "@/components/cobrain/AmbientBackdrop";
import { ReserveModal } from "@/components/cobrain/ReserveModal";
import { ExitIntent } from "@/components/cobrain/ExitIntent";
import { TestimonialWall } from "@/components/cobrain/TestimonialWall";
import { IndustryResonance } from "@/components/cobrain/IndustryResonance";
import { RoiStrip } from "@/components/cobrain/RoiStrip";
import { HeroParticles } from "@/components/cobrain/HeroParticles";
import { CompareMatrix } from "@/components/cobrain/CompareMatrix";
import { SpoofVideo } from "@/components/cobrain/SpoofVideo";
import { EmployeeSliderPricing } from "@/components/cobrain/EmployeeSliderPricing";
import { OrgPulse } from "@/components/cobrain/OrgPulse";
import { PartnerMarquee } from "@/components/cobrain/PartnerMarquee";
import { openCheckout } from "@/lib/startCheckout";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";

/* ============================================================
   COBRAIN — second product page (Loop 3 enhanced)
   Mirrors the homepage cream-theme design system.
   Self-contained: local PALETTE / FONT_*, local nav/footer,
   local mobile buy bar, ticker, FAQ, before/after, etc.
   ============================================================ */

/* ---------- LED dots row ---------- */
const LedRow = () => (
  <div aria-hidden className="cb-led-row" style={{ display: "flex", gap: 6, marginBottom: 14 }}>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{
        width: 6, height: 6, borderRadius: "50%",
        background: PALETTE.amber, opacity: 0.55,
        animation: `cb-led 1.6s ${i * 0.25}s ease-in-out infinite`,
      }} />
    ))}
  </div>
);

/* ---------- SectionHead ---------- */
const SectionHead = ({ n, eyebrow, title, italic, lede, dark = false }: {
  n: string; eyebrow: string; title: React.ReactNode; italic?: string; lede?: string; dark?: boolean;
}) => {
  const ink = dark ? "#fff" : PALETTE.ink;
  const mute = dark ? "rgba(255,255,255,0.55)" : PALETTE.muted;
  const ink2 = dark ? "rgba(255,255,255,0.7)" : PALETTE.ink2;
  return (
    <div className="cb-section-head" style={{ marginBottom: 56 }}>
      <LedRow />
      <div className="cb-section-head-meta" style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18 }}>
        <span className="cb-section-head-num" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 32, color: PALETTE.amber, lineHeight: 1 }}>{n}</span>
        <span className="cb-section-head-eyebrow" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: mute, fontWeight: 500 }}>{eyebrow}</span>
      </div>
      <h2 className="cb-section-head-title" style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(30px, 5vw, 60px)", lineHeight: 1.08, letterSpacing: "-0.015em", margin: 0, color: ink, textWrap: "balance" }}>
        {title} {italic && <em style={{ color: PALETTE.amber }}>{italic}</em>}
      </h2>
      {lede && (
        <p className="cb-section-head-lede" style={{ marginTop: 24, fontSize: 17, lineHeight: 1.55, color: ink2, maxWidth: 720 }}>{lede}</p>
      )}
    </div>
  );
};

/* ---------- Reveal wrapper ---------- */
const Reveal = ({ children, delay = 0, as: Tag = "div", ...rest }: any) => {
  const ref = useReveal<HTMLDivElement>();
  return (
    <Tag
      ref={ref}
      data-reveal
      style={{
        opacity: 0,
        transform: "translateY(16px)",
        transition: `opacity 700ms ${delay}ms cubic-bezier(.2,.8,.2,1), transform 700ms ${delay}ms cubic-bezier(.2,.8,.2,1)`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* ---------- Hero install-target ticker ---------- */
const INSTALL_POOL = [
  "a 12-person event company",
  "a 6-person agency",
  "a 4-person contractor team",
  "a solo founder w/ 2 ops",
  "an 8-person consulting firm",
  "a 5-person field service team",
  "a 10-person studio",
  "a 3-person professional practice",
];
const HeroChip = () => {
  const [i, setI] = useState(0);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % INSTALL_POOL.length), 3200);
    return () => clearInterval(t);
  }, [reduced]);
  return (
    <div data-hero-ticker style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "nowrap",
      padding: "6px 14px", borderRadius: 999,
      width: "fit-content",
      background: "rgba(196,130,58,0.10)",
      border: "1px solid rgba(196,130,58,0.22)",
      fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.10em",
      textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700,
      marginBottom: 24, maxWidth: "100%", minWidth: 0,
      overflow: "hidden",
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: PALETTE.amber, animation: "cb-led 1.6s ease-in-out infinite" }} />
        Now installing for
      </span>
      <span aria-hidden style={{ opacity: 0.4, flexShrink: 0 }}>·</span>
      <span key={i} style={{
        animation: reduced ? undefined : "cb-fade 320ms cubic-bezier(.2,.8,.2,1) both",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        minWidth: 0, flex: "0 1 auto",
      }}>
        {INSTALL_POOL[i]}
      </span>
    </div>
  );
};

/* ---------- Full site Nav (mirrors homepage) with CoBrain marker ---------- */
const Nav = () => (
  <SiteNav
    product="cobrain"
    primary={{ label: "Meet Eva", to: "/#pricing" }}
    callSource="cobrain_nav"
  />
);

/* ---------- Local Footer ---------- */
const Footer = () => <SiteFooter product="cobrain" callSource="cobrain_footer" />;

/* ---------- FAQ accordion ---------- */
const FAQ_ITEMS = [
  { q: "Is this just a chatbot?", a: "No. CoBrain does work — drafts, sends, schedules, follows up — under your approval rules. Conversation is the surface; action is the point." },
  { q: "Will it replace my CRM?", a: "No. CoBrain plugs into the CRM, email, calendar, and docs you already use. It's the layer that makes them work together, not a replacement." },
  { q: "How is this different from hiring Eva alone?", a: "Eva alone is the operator — she does the work. CoBrain is the operating layer underneath: company memory, approval rules, reusable skills, and Watchtower. Together they install your business as a system Eva can actually run." },
  { q: "Does CoBrain replace my team?", a: "No. CoBrain gives each employee their own agent — a copilot that handles the repeatable work that was already falling through the cracks. Your team keeps the judgment, customer relationships, and decisions. CoBrain just stops the dropped balls." },
  { q: "My team won't adopt it.", a: "They don't have to change tools. Eva works inside the email, calendar, and docs they already use. The change is what stops falling through the cracks — not what they have to learn." },
  { q: "What happens to my data?", a: "Your data stays in your dedicated business environment. Nothing is used to train shared models. Fleet Intelligence is opt-in and only shares reusable skill patterns — never customer data." },
  { q: "Who maintains the workflows?", a: "We do. Setup, onboarding, ongoing improvements, and support are all included in your engagement. You stay focused on the business; we keep CoBrain useful." },
  { q: "Can I bring my own tools?", a: "Yes. CoBrain plugs into the email, calendar, docs, CRM, and invoicing tools you already use. No rip-and-replace." },
  { q: "What does it cost?", a: "CoBrain is deployed as part of your engagement with an engineer. Engagements start at $1,500/mo, scoped to your team size and workflows." },
  { q: "What happens after the first 3 months?", a: "Your company keeps running on its new AI workflows. We keep it hosted, maintained, and upgraded — and scope new projects whenever you want them." },
  { q: "Do you only use your own software?", a: "Eva and CoBrain are our core. Where another tool fits a job better, we use it — the job is solving the problem." },
  { q: "Do I need technical staff?", a: "No. The owner needs to be willing to set approval rules and review the first workflows. That's it. We handle the rest." },
  { q: "Do useful workflows get reused — across my business or others?", a: "Yes. Every workflow we build becomes a reusable skill inside your CoBrain. Fleet Intelligence is opt-in: you can share reusable skill patterns with the cohort, never customer data." },
];

const FAQ = () => {
  const items = FAQ_ITEMS;
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div data-faq-accordion style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={it.q} style={{ background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 16, overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                padding: "18px 22px", background: "transparent", border: 0, cursor: "pointer",
                fontSize: 15.5, fontWeight: 600, color: PALETTE.ink, fontFamily: FONT_BODY, textAlign: "left",
              }}
            >
              <span>{it.q}</span>
              <span aria-hidden style={{ color: PALETTE.amber, fontSize: 22, lineHeight: 1, transition: "transform 220ms", transform: isOpen ? "rotate(45deg)" : "rotate(0)" }}>+</span>
            </button>
            <div style={{
              maxHeight: isOpen ? 320 : 0, overflow: "hidden",
              transition: "max-height 320ms cubic-bezier(.2,.8,.2,1)",
            }}>
              <div style={{ padding: "0 22px 20px", fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55 }}>
                {it.a}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================
   PAGE
   ============================================================ */

function CoBrain() {
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveSource, setReserveSource] = useState("cobrain-reserve");
  const [employees, setEmployees] = useState(10);
  const showHeroVideo = useMediaQuery("(min-width: 641px) and (prefers-reduced-motion: no-preference)");
  const isMobileViewport = useMediaQuery("(max-width: 760px)");
  const openReserve = useCallback((source = "cobrain-reserve") => {
    setReserveSource(source);
    setReserveOpen(true);
    track("reserve_open", { source });
  }, []);

  const startCobrainCheckout = useCallback(async (seats: number) => {
    track("checkout_click", { seats });
    const err = await openCheckout({
      plan: "cobrain",
      cycle: "monthly",
      seats,
      successPath: "/cobrain?checkout=success&session_id={CHECKOUT_SESSION_ID}",
      cancelPath: "/cobrain#package",
    });
    return err;
  }, []);

  useEffect(() => {
    document.title = "CoBrain — the company brain for small service businesses";
    const setMeta = (sel: string, attr: string, name: string, content: string) => {
      let el = document.querySelector(`${sel}[${attr}="${name}"]`) as HTMLMetaElement | HTMLLinkElement | null;
      if (!el) {
        el = document.createElement(sel) as any;
        el!.setAttribute(attr, name);
        document.head.appendChild(el!);
      }
      if (sel === "meta") (el as HTMLMetaElement).setAttribute("content", content);
      else (el as HTMLLinkElement).setAttribute("href", content);
    };
    setMeta("meta", "name", "description", "CoBrain turns your business into one operating memory Eva can run. Follow-ups, scheduling, prep, handoffs — deployed by an engineer.");
    setMeta("link", "rel", "canonical", "https://www.hireeva.io/cobrain");
    // Open Graph
    setMeta("meta", "property", "og:title", "CoBrain — the company brain for small businesses");
    setMeta("meta", "property", "og:description", "Your whole company, integrated. Eva runs on top of CoBrain — deployed by your engineer.");
    setMeta("meta", "property", "og:type", "product");
    setMeta("meta", "property", "og:url", "https://www.hireeva.io/cobrain");
    setMeta("meta", "property", "og:image", "https://www.hireeva.io/og-evaos.png");
    setMeta("meta", "name", "twitter:card", "summary_large_image");

    // JSON-LD Product schema
    const ldId = "cobrain-jsonld";
    let script = document.getElementById(ldId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = ldId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "CoBrain",
        description: "The operating memory layer for small service businesses. Eva runs on top of CoBrain.",
        brand: { "@type": "Brand", name: "evaOS" },
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "CoBrain",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android, WhatsApp, SMS, Email, Voice",
        url: "https://www.hireeva.io/cobrain",
        description:
          "CoBrain is the operating memory layer for small service businesses — follow-ups, scheduling, prep and handoffs, deployed by an engineer.",
        provider: { "@type": "Organization", name: "ElectricSheep", url: "https://www.hireeva.io/" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      },
    ]);

    return () => {
      const s = document.getElementById(ldId);
      if (s) s.remove();
    };
  }, []);

  // Intercom collision: lift the launcher above sticky CTAs while on /cobrain
  // and suppress any auto-opened message on first paint. Restore on unmount.
  useEffect(() => {
    const w = window as any;
    const tryUpdate = () => {
      if (typeof w.Intercom !== "function") return false;
      try {
        w.Intercom("update", { vertical_padding: 20, horizontal_padding: 20 });
        w.Intercom("hide");
      } catch { /* noop */ }
      return true;
    };
    if (!tryUpdate()) {
      // Intercom may load late — poll briefly
      const id = window.setInterval(() => { if (tryUpdate()) window.clearInterval(id); }, 400);
      window.setTimeout(() => window.clearInterval(id), 8000);
      return () => window.clearInterval(id);
    }
    return () => {
      try { (window as any).Intercom?.("update", { vertical_padding: 20, horizontal_padding: 20 }); } catch { /* noop */ }
    };
  }, []);

  const tiles = [
    { title: "Company memory", body: "Email, calendars, docs, invoices, customer notes, workflows, and operating patterns become usable context." },
    { title: "Eva operators", body: "Eva handles repeatable work: follow-ups, scheduling, reminders, prep, handoffs, and admin tasks." },
    { title: "Approval gates", body: "The owner stays in control. Risky actions pause for review before anything goes out." },
    { title: "Watchtower", body: "Background monitoring for dropped balls, missed replies, reminders, follow-ups, and exceptions." },
  ];

  const before = [
    "Leads cool off in the inbox before anyone replies.",
    "Follow-ups depend on whether someone remembers.",
    "The owner is still answering email at 11pm.",
    "Customer context lives in five different tabs.",
    "Repeat work gets re-done from scratch every time.",
  ];
  const after = [
    "Eva drafts replies overnight, ready by 8am.",
    "Follow-ups happen on schedule, not by memory.",
    "Owner reviews & approves — then closes the laptop.",
    "Customer context is one prompt away, always current.",
    "Wins become reusable skills the business keeps.",
  ];

  // (install motion now uses the Hour 0/12/24 rail directly)

  return (
    <div className="cb-main" style={{ background: PALETTE.bg, color: PALETTE.ink, fontFamily: FONT_BODY, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @keyframes cb-led { 50% { opacity: 0.35 } }
        @keyframes cb-fade { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
        @keyframes cb-slow-zoom { 0% { transform: scale(1) } 50% { transform: scale(1.06) } 100% { transform: scale(1) } }
        @keyframes cb-caret { 50% { opacity: 0 } }
        @keyframes cb-drift-1 { 0% { transform: translate(0,0) } 50% { transform: translate(40px, -30px) } 100% { transform: translate(0,0) } }
        @keyframes cb-drift-2 { 0% { transform: translate(0,0) } 50% { transform: translate(-50px, 20px) } 100% { transform: translate(0,0) } }
        .cb-drift   { animation: cb-drift-1 22s ease-in-out infinite; }
        .cb-drift-2 { animation: cb-drift-2 28s ease-in-out infinite; }
        [data-reveal][data-revealed="true"] { opacity: 1 !important; transform: none !important; }
        @media (prefers-reduced-motion: reduce) {
          [data-reveal] { opacity: 1 !important; transform: none !important; transition: none !important; }
          .cb-drift, .cb-drift-2 { animation: none !important; }
        }
        @media (max-width: 880px) {
          .cb-hero-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 32px !important; }
          .cb-hero-console { order: 2; }
        }
        @media (max-width: 640px) {
          html, body { overflow-x: hidden; }
          [data-hero-ticker] { font-size: 10px !important; max-width: calc(100vw - 40px) !important; }
          [data-hero-ticker] > span:last-child { max-width: calc(100vw - 200px) !important; }
          .cb-hero-credit-line { font-size: 10.5px !important; letter-spacing: 0.04em !important; }
          .cb-hero-credit-line br { display: inline; }
        }
        @media (max-width: 700px) {
          .cb-hero-cta { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          .cb-hero-cta > button { width: 100%; justify-content: center; }
          .cb-hero-cta > a { justify-content: center; padding: 6px 0 !important; font-size: 13px !important; }
          .cb-section-head { margin-bottom: 36px !important; }
          .cb-section-head-num { font-size: 24px !important; }
          .cb-section-head-eyebrow { font-size: 10.5px !important; }
          .cb-section-head-lede { font-size: 15px !important; margin-top: 18px !important; }
          /* Mobile centering pass — unify alignment across the page */
          .cb-main h1, .cb-main h2 { text-align: center !important; }
          .cb-main .cb-hero-grid > div > p { text-align: center !important; margin-left: auto !important; margin-right: auto !important; }
          .cb-section-head { text-align: center !important; }
          .cb-section-head .cb-section-head-meta { justify-content: center !important; }
          .cb-section-head-lede { margin-left: auto !important; margin-right: auto !important; }
          .cb-led-row { justify-content: center !important; }
          .cb-industry-chips { justify-content: center !important; }
          .cb-industry-vignette > div { border-left: none !important; text-align: center !important; align-items: center !important; }
          .cb-industry-vignette > div > div { justify-content: center !important; }
          .cb-tile { text-align: center !important; align-items: center !important; }
          .cb-package-meta { text-align: center !important; align-items: center !important; justify-content: center !important; }
          [data-testimonial-wall] .cb-hero-quote { text-align: center; }
          [data-testimonial-wall] .cb-hero-quote > div > div:last-child { justify-content: center; }
          [data-testimonial-wall] .cb-hero-quote span[style*="align-self"] { align-self: center !important; }
        }
        .cb-vs-table { width: 100%; border-collapse: separate; border-spacing: 0; }
        .cb-vs-table th, .cb-vs-table td { padding: 14px 16px; text-align: left; font-size: 14px; border-bottom: 1px solid ${PALETTE.line}; }
        .cb-vs-table th { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: ${PALETTE.muted}; font-weight: 700; }
        .cb-vs-table .cb-vs-cobrain { background: rgba(196,130,58,0.06); color: ${PALETTE.ink}; font-weight: 600; }
        .cb-vs-table thead .cb-vs-cobrain { color: ${PALETTE.amber}; }
        .cb-vs-table-dark th, .cb-vs-table-dark td { border-bottom: 1px solid rgba(255,255,255,0.08); }
        .cb-vs-table-dark .cb-vs-cobrain { background: rgba(196,130,58,0.14); color: #fff !important; font-weight: 600; }
        .cb-vs-table-dark thead .cb-vs-cobrain { color: ${PALETTE.amberSoft} !important; background: rgba(196,130,58,0.20); }
        @media (max-width: 720px) {
          .cb-rail-grid { grid-template-columns: 1fr !important; }
          .cb-rail-line { display: none; }
        }
        @media print {
          [data-cb-no-print] { display: none !important; }
        }
      `}</style>
      <Nav />

      <main id="page-main">
      {/* HERO */}
      <section style={{ padding: "clamp(140px, 18vw, 220px) clamp(20px, 5vw, 24px) clamp(48px, 10vw, 88px)", position: "relative", overflow: "hidden" }}>
        {showHeroVideo && (
          <>
            <video
              aria-hidden
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260511_230229_7c9bc431-46cf-489a-948d-e8144d8eb5d4.mp4"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "cover", zIndex: 0, opacity: 0.40,
                mixBlendMode: "luminosity", pointerEvents: "none",
              }}
            />
            <div aria-hidden style={{
              position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
              background: `linear-gradient(180deg, ${PALETTE.bg} 0%, rgba(245,240,231,0.55) 35%, ${PALETTE.bg} 100%)`,
            }} />
          </>
        )}
        <AmbientBackdrop />
        <HeroParticles />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <div className="cb-hero-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.7fr)", gap: 48, alignItems: "center" }}>
          <div>
          <h1 style={{
            fontFamily: FONT_DISPLAY, fontWeight: 400,
            fontSize: "clamp(36px, 8.5vw, 96px)", lineHeight: 1.05,
            letterSpacing: "-0.02em", margin: 0, color: PALETTE.ink,
            textWrap: "balance",
          }}>
            Give your whole company{" "}
            <em style={{ color: PALETTE.amber }}>a brain.</em>
          </h1>
          <p style={{ marginTop: 24, fontSize: "clamp(16px, 4vw, 19px)", lineHeight: 1.55, color: PALETTE.ink2, maxWidth: 720 }}>
            Eva runs alongside the owner. CoBrain runs across the company — every employee's email, CRM, and calendar plugged into one shared brain.
          </p>

          <div data-inline-cta className="cb-hero-cta" style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <a href="#package" onClick={() => track("hero_cta_click")} style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "14px 26px", background: PALETTE.ink, color: "#fff",
              borderRadius: 999, textDecoration: "none", fontSize: 14, fontWeight: 600, minHeight: 48,
            }}>
              See what's included <ArrowRight size={15} />
            </a>
            <button type="button" onClick={() => openReserve("cobrain-hero")} style={{
              display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: 0,
              padding: "14px 8px", color: PALETTE.ink2, fontSize: 14, fontWeight: 500, cursor: "pointer",
              fontFamily: FONT_BODY, textDecoration: "underline",
            }}>
              or talk to an engineer first
            </button>
          </div>
          </div>
          <div className="cb-hero-console" style={{ display: "flex", justifyContent: "center" }}>
            <HeroConsole />
          </div>
          </div>

        </div>
      </section>

      {/* INDUSTRY RESONANCE — pick your business, see the day */}
      <section style={{ padding: "clamp(44px, 9vw, 80px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="01"
              eyebrow="See yourself in it"
              title="Pick your business."
              italic="See the day."
              lede="CoBrain installs differently for a roofer than a dentist than a law firm. Same brain — your business's vocabulary, calendar, and judgment."
            />
          </Reveal>
          <Reveal delay={80}>
            <IndustryResonance />
          </Reveal>
        </div>
      </section>

      {/* ORG PULSE — interactive hub-and-spoke */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="02"
              eyebrow="Live signal flow"
              title="One brain. Many agents."
              italic="Real signals, real actions."
              lede="Watch the company breathe. Each spoke is an employee's agent — sales, ops, finance, support, owner. Signals collapse to the hub, decisions push back out."
            />
          </Reveal>
          <Reveal delay={80}>
            <OrgPulse />
          </Reveal>
        </div>
      </section>

      {/* WHAT COBRAIN GIVES */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="03"
              eyebrow="What CoBrain installs"
              title="The operating layer your"
              italic="business has been missing."
            />
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {tiles.map((t, i) => (
              <Reveal key={t.title} delay={i * 60}>
                <div className="cb-tile" style={{ background: PALETTE.card, borderRadius: 20, padding: 28, border: `1px solid ${PALETTE.line}`, height: "100%" }}>
                  <h3 style={{ fontFamily: FONT_BODY, fontSize: 17, fontWeight: 600, color: PALETTE.ink, margin: "0 0 10px", lineHeight: 1.3 }}>{t.title}</h3>
                  <p style={{ fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55, margin: 0 }}>{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* T4 — BEFORE / AFTER */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="04"
              eyebrow="What changes on day one"
              title="Before CoBrain. After"
              italic="CoBrain."
              lede="The same business, the same team, the same tools — running on a memory layer instead of in someone's head."
            />
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
            <Reveal>
              <div style={{ background: PALETTE.card, borderRadius: 20, padding: 28, border: `1px solid ${PALETTE.line}`, height: "100%" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 14 }}>Before</div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                  {before.map((b) => (
                    <li key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55 }}>
                      <XMark /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div style={{ background: PALETTE.dark, color: "#fff", borderRadius: 20, padding: 28, border: `1px solid ${PALETTE.line}`, position: "relative", overflow: "hidden", height: "100%" }}>
                <div aria-hidden style={{ position: "absolute", inset: "-30% -20% auto auto", width: 320, height: 320, background: `radial-gradient(circle, ${PALETTE.amber}33 0%, transparent 60%)`, filter: "blur(40px)", pointerEvents: "none" }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 14 }}>After</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {after.map((a) => (
                      <li key={a} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14.5, color: "rgba(255,255,255,0.88)", lineHeight: 1.55 }}>
                        <Check size={14} color={PALETTE.amberSoft} /> <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL WALL — SMB owner social proof */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="05"
              eyebrow="What owners actually say"
              title="The owners who installed it"
              italic="stopped running the business in their head."
              lede="Composite quotes from the beta cohort. Different verticals, same pattern: less owner overhead, fewer dropped balls, and a business that finally remembers itself."
            />
          </Reveal>
          <Reveal delay={80}>
            <TestimonialWall />
          </Reveal>
          <Reveal delay={140}>
            <div style={{ marginTop: 32 }}>
              <PartnerMarquee />
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSTALL MOTION */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="06"
              eyebrow="The install motion"
              title="First workflow in 24 hours."
              italic="Full system over time."
            />
          </Reveal>
          {/* 24-hour install rail (was duplicated in package; canonical home is here) */}
          <Reveal delay={60}>
            <div className="cb-rail" style={{ position: "relative" }}>
              <div aria-hidden className="cb-rail-line" style={{
                position: "absolute", top: 12, left: 18, right: 18, height: 2,
                backgroundImage: `linear-gradient(to right, ${PALETTE.amber} 50%, transparent 0%)`,
                backgroundSize: "10px 2px", backgroundRepeat: "repeat-x",
                opacity: 0.55,
              }} />
              <div className="cb-rail-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, position: "relative" }}>
                {[
                  { h: "Hour 0", t: "We map the business — tools, context, owner approval rules." },
                  { h: "Hour 12", t: "First workflow drafted; owner reviews and tightens the gates." },
                  { h: "Hour 24", t: "Eva running live across the team with approval gates in place." },
                ].map((row, i) => (
                  <div key={row.h} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 14 }}>
                    <span aria-hidden style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: PALETTE.bg, border: `2px solid ${PALETTE.amber}`,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      position: "relative", zIndex: 1,
                    }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: PALETTE.amber, animation: `cb-led 1.6s ${i * 0.3}s ease-in-out infinite` }} />
                    </span>
                    <div style={{ background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 16, padding: "16px 18px", width: "100%" }}>
                      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 6 }}>{row.h}</div>
                      <div style={{ fontSize: 14, color: PALETTE.ink2, lineHeight: 1.5 }}>{row.t}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* L2-4 — vs DIY comparison */}

      <section style={{ padding: "clamp(56px, 10vw, 96px) clamp(20px, 5vw, 24px)", background: PALETTE.dark, color: "#fff", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: "-20% -10% auto auto", width: 520, height: 520,
          background: `radial-gradient(circle, ${PALETTE.amber}26 0%, transparent 60%)`,
          filter: "blur(60px)", pointerEvents: "none",
        }} />
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <Reveal>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", fontFamily: FONT_MONO, fontWeight: 700, marginBottom: 14 }}>How it compares</div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(32px, 4vw, 48px)", lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 12px", color: "#fff" }}>
              The alternatives, <em style={{ color: PALETTE.amber }}>side by side.</em>
            </h2>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,0.7)", lineHeight: 1.55, margin: "0 0 32px", maxWidth: 640 }}>
              Owners try a few things before they land here. Here's the honest tradeoff.
            </p>
          </Reveal>
          <Reveal>
            <CompareMatrix />
          </Reveal>
          <Reveal delay={80}><SpoofVideo /></Reveal>
        </div>
      </section>

      {/* ROI STRIP — interactive payback math */}
      <section style={{ padding: "clamp(44px, 9vw, 80px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="07"
              eyebrow="The math"
              title="Capacity returned across"
              italic="your whole company."
              lede="Two dials: average loaded employee cost and capacity uplift. The math is yours — the answer almost always points the same direction."
            />
          </Reveal>
          <Reveal delay={80}>
            <RoiStrip employees={employees} />
          </Reveal>
        </div>
      </section>

      {/* PACKAGE + PRICE */}
      <section id="package" style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bg }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="08"
              eyebrow="The package"
              title="One brain, every employee."
              italic="Part of your deployment, scoped on a call."
            />
          </Reveal>
          <Reveal>
            <EmployeeSliderPricing
              onReserve={() => openReserve("cobrain-package")}
              onCheckout={(seats) => startCobrainCheckout(seats)}
              employees={employees}
              onEmployeesChange={setEmployees}
            />
          </Reveal>

          <p style={{
            marginTop: 32, textAlign: "center",
            fontFamily: FONT_DISPLAY, fontStyle: "italic",
            fontSize: 15, color: PALETTE.muted, lineHeight: 1.5,
          }}>
            Cortex is the technical substrate underneath CoBrain.
          </p>
        </div>
      </section>

      {/* T6 — FAQ */}
      <section style={{ padding: "clamp(44px, 10vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <Reveal>
            <SectionHead
              n="09"
              eyebrow="Common questions"
              title="What owners ask"
              italic="before they install."
            />
          </Reveal>
          <Reveal delay={80}>
            <FAQ />
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-final-cta style={{ padding: "clamp(64px, 12vw, 120px) clamp(20px, 5vw, 24px) clamp(80px, 14vw, 120px)", background: PALETTE.bgAlt, textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: "-20% -10%",
          background: `radial-gradient(circle at center, ${PALETTE.amberSoft}22 0%, transparent 55%)`,
          filter: "blur(60px)", pointerEvents: "none", zIndex: 0,
          animation: "cb-slow-zoom 22s ease-in-out infinite",
        }} />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h2 style={{
            fontFamily: FONT_DISPLAY, fontStyle: "italic",
            fontSize: "clamp(44px, 8vw, 96px)", lineHeight: 1.02,
            color: PALETTE.ink, margin: 0, fontWeight: 400, letterSpacing: "-0.02em",
          }}>
            Your company, integrated.
          </h2>
          <p style={{ marginTop: 28, fontSize: 18, color: PALETTE.ink2, lineHeight: 1.5, maxWidth: 580, margin: "28px auto 0" }}>
            Stop running the business in your head. Give it a brain — and let Eva do the work.
          </p>
          <div data-inline-cta className="cb-hero-cta" style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#package" onClick={() => track("final_cta_click")} style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "14px 26px", background: PALETTE.ink, color: "#fff",
              borderRadius: 999, textDecoration: "none", fontSize: 14, fontWeight: 600,
              minHeight: 48,
            }}>
              See what's included <ArrowRight size={15} />
            </a>
            <button type="button" onClick={() => openReserve("cobrain-final")} style={{
              display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: 0,
              padding: "14px 8px", color: PALETTE.ink2, fontSize: 14, fontWeight: 500, cursor: "pointer",
              fontFamily: FONT_BODY, textDecoration: "underline",
            }}>
              or talk to an engineer first
            </button>
          </div>
        </div>
      </section>

      </main>
      <Footer />
      <div data-cb-no-print>
        {/* CoBrain exit-intent popup disabled per product request. Keep import + component for future re-enable.
        {!isMobileViewport && <ExitIntent onReserve={() => openReserve("cobrain-exit")} />}
        */}
      </div>
      <ReserveModal open={reserveOpen} onClose={() => setReserveOpen(false)} source={reserveSource} />
    </div>
  );
}

export default CoBrain;
